/**
 * IRONMASTER PACKAGE BUILDER — SIDE CART (IRON-10, v1)
 * =====================================================
 * A persistent right-column cart summary for the Package Builder page,
 * matching the "CART PACKAGE C" Figma reference (a fixed sidebar, not a
 * slide-out drawer). Reflects the REAL Shopify cart via /cart.js and
 * /cart/change.js — separate from .pkg-summary above, which only tracks
 * the in-progress selections before "Add package to cart" is clicked.
 *
 * Re-uses window.IronmasterDiscount (assets/ironmaster-discount.js) for the
 * discount preview, so the same single source of truth drives the cart
 * drawer, the full cart page, and this sidebar. The real discount is only
 * ever applied at checkout by the Shopify Function — everything here is a
 * preview, same caveat as the rest of the discount UI.
 *
 * v1 scope / known limitations:
 *   - Discount code field submits via the standard Shopify `/discount/:code`
 *     redirect (Shopify has no pure-AJAX apply-code endpoint that updates
 *     cart totals without a navigation), so it still leaves this page — but
 *     redirects to /cart rather than straight to checkout, so the customer
 *     sees it applied on the real cart page (via snippets/cart-discount.liquid)
 *     before committing to checkout.
 *   - Checkout button is a plain link to /checkout (native Shopify flow).
 */
(function () {
  "use strict";

  function escapeHtml(s) {
    return String(s).replace(/[&<>"]/g, function (c) {
      return { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c];
    });
  }

  function money(cents) {
    var amount = (cents / 100).toFixed(2);
    var moneyFormat = (window.Shopify && window.Shopify.money_format) || "${{amount}}";
    return moneyFormat.replace(/\{\{\s*amount\s*\}\}/, amount).replace(/\{\{\s*amount_no_decimals\s*\}\}/, Math.round(cents / 100));
  }

  var PACKAGE_DISCOUNT_RE = /everyday package discount/i;

  function isPackageApplication(app) {
    return PACKAGE_DISCOUNT_RE.test((app && app.title) || "");
  }

  function packageFirst(a, b) {
    return Number(isPackageApplication(b)) - Number(isPackageApplication(a));
  }

  function discountLabel(app, format) {
    var title = String(app.title || "");
    var name;
    if (isPackageApplication(app)) name = title.split(" — ").pop().trim();
    else if (app.type === "discount_code") name = (format === "row" ? "Coupon " : "") + title.toUpperCase();
    else name = title;
    if (app.value_type !== "percentage") return name;
    var percent = Math.round(parseFloat(app.value) * 100) / 100;
    return name + (format === "row" ? " — " + percent + "% off" : " " + percent + "%");
  }

  function cartDiscountApplications(cart) {
    var seen = {};
    var list = [];
    function add(app) {
      if (!app || seen[app.key]) return;
      seen[app.key] = true;
      list.push(app);
    }
    (cart.items || []).forEach(function (item) {
      (item.line_level_discount_allocations || []).forEach(function (allocation) {
        add(allocation.discount_application);
      });
    });
    (cart.cart_level_discount_applications || []).forEach(add);
    return list.sort(packageFirst);
  }

  var compareAtCache = {};
  var fetchedHandles = {};

  function cacheCompareAtFromProduct(product) {
    if (!product || !product.variants) return;
    product.variants.forEach(function (variant) {
      var compareAt = variant.compare_at_price;
      compareAtCache[String(variant.id)] =
        compareAt && compareAt > variant.price ? compareAt : 0;
    });
  }

  function fetchCompareAtForHandles(handles) {
    var pending = [];
    (handles || []).forEach(function (handle) {
      if (!handle || fetchedHandles[handle]) return;
      fetchedHandles[handle] = true;
      pending.push(
        fetch("/products/" + encodeURIComponent(handle) + ".js", { headers: { Accept: "application/json" } })
          .then(function (r) { return r.ok ? r.json() : null; })
          .then(cacheCompareAtFromProduct)
          .catch(function () { fetchedHandles[handle] = false; })
      );
    });
    return Promise.all(pending);
  }

  function priceBlock(item) {
    var compareAtUnit = compareAtUnitPrice(item);
    var row = '<p class="pkg-cart__item-price">' + money(item.original_line_price) + '</p>';
    var savings = 0;

    if (compareAtUnit) {
      savings = Math.round((compareAtUnit - item.original_price) * 100 / compareAtUnit);
      row += '<p class="pkg-cart__item-compare">' +
               '<span class="sr-only">Regular price </span>' +
               money(compareAtUnit * item.quantity) +
             '</p>';
    }

    var html = '<div class="pkg-cart__item-price-row">' + row + '</div>';

    if (savings > 0) {
      html += '<p class="pkg-cart__item-savings">' + savings + '% off</p>';
    }

    return '<div class="pkg-cart__item-prices">' + html + '</div>';
  }

  function compareAtUnitPrice(item) {
    var IM = window.IronmasterDiscount;
    if (IM && IM.compareAtPriceForVariant) {
      var fromEngine = IM.compareAtPriceForVariant(item.variant_id);
      if (fromEngine) return fromEngine;
    }
    return compareAtCache[String(item.variant_id)] || 0;
  }

  function init(root) {
    var itemsEl = root.querySelector("[data-pkg-cart-items]");
    var emptyEl = root.querySelector("[data-pkg-cart-empty]");
    var subtotalEl = root.querySelector("[data-pkg-cart-subtotal]");
    var totalRowEl = root.querySelector(".pkg-cart__row--total");
    var totalEl = root.querySelector("[data-pkg-cart-total]");
    var codeForm = root.querySelector("[data-pkg-cart-code-form]");
    var checkoutLink = root.querySelector(".pkg-cart__checkout");

    var errorEl = root.querySelector("[data-pkg-cart-error]");
    var clearBtn = root.querySelector(".cart-clear-button");

    var busy = false;

    function setCartError(message) {
      if (!errorEl) return;
      errorEl.textContent = message || "";
      errorEl.hidden = !message;
    }

    function cartLineToEngineLine(item, resolvedTags) {
      return {
        id: item.key,
        quantity: item.quantity,
        merchandise: {
          sku: item.sku || "",
          // Full/regular price — per Víctor's spec (16-Jul), every line always
          // shows and qualifies at full price; the discount is only ever an
          // aggregate deduction at the bottom, never baked into a line itself.
          price: { amount: (item.original_price / 100).toFixed(2) },
          product: {
            id: String(item.product_id),
            title: item.product_title,
            // /cart.js's product_tags isn't reliably populated with the anchor tags
            // (im-anchor-rack-3000 etc.) on this catalog, so — matching the same
            // workaround package-builder.js already uses for its pre-add-to-cart
            // preview — always fold in the SKU-synthesized anchor tag too.
            tags: (resolvedTags && resolvedTags.length ? resolvedTags.slice() : (item.product_tags ? item.product_tags.split(", ").map(function (t) { return t.trim(); }) : []))
              .concat(window.IronmasterDiscount ? window.IronmasterDiscount.tagsForSku(item.sku) : [])
          }
        }
      };
    }

    // Per Víctor's spec (16-Jul): full/regular price shows on every line —
    // the discount only ever appears as one aggregate row at the bottom —
    // and qualifying lines get a gold "PACKAGE · X% OFF" badge naming the
    // active package's cut. Only one package (the best-savings match) is
    // ever active at a time, so every badge always shows the same %.
    function renderItems(cart) {
      itemsEl.innerHTML = "";
      var hasItems = cart.items && cart.items.length > 0;
      emptyEl.hidden = hasItems;
      itemsEl.hidden = !hasItems;
      if (clearBtn) clearBtn.hidden = !hasItems;
      if (!hasItems) return;

      cart.items.forEach(function (item) {
        var row = document.createElement("div");
        row.className = "pkg-cart__item";
        var badges = (item.line_level_discount_allocations || [])
          .map(function (allocation) { return allocation.discount_application; })
          .filter(Boolean)
          .sort(packageFirst)
          .map(function (app) { return '<span class="pkg-cart__item-badge">' + escapeHtml(discountLabel(app, "badge")) + '</span>'; });
        var badge = badges.length ? '<div class="pkg-cart__item-badges">' + badges.join("") + '</div>' : '';
        var itemUrl = escapeHtml(item.url || "#");
        row.innerHTML =
          '<a class="pkg-cart__item-link" href="' + itemUrl + '" aria-hidden="true" tabindex="-1">' +
            (item.image
              ? '<img class="pkg-cart__item-img" src="' + item.image + '&width=160" alt="" loading="lazy">'
              : '<span class="pkg-cart__item-img pkg-cart__item-img--placeholder"></span>') +
          '</a>' +
          '<div class="pkg-cart__item-info">' +
            badge +
            '<a class="pkg-cart__item-title" href="' + itemUrl + '">' + escapeHtml(item.product_title) + (item.variant_title ? ' <span class="pkg-cart__item-variant">(' + escapeHtml(item.variant_title) + ')</span>' : '') + '</a>' +
            '<div class="pkg-cart__item-controls">' +
              '<div class="pkg-cart__item-qty">' +
                '<button type="button" data-cart-qty-dec aria-label="Decrease quantity">−</button>' +
                '<span data-cart-qty-value>' + item.quantity + '</span>' +
                '<button type="button" data-cart-qty-inc aria-label="Increase quantity">+</button>' +
              '</div>' +
              '<button type="button" class="pkg-cart__item-remove" data-cart-item-remove aria-label="Remove ' + escapeHtml(item.product_title) + ' from your cart">' +
                '<svg width="12" height="12" viewBox="0 0 10 10" fill="none" aria-hidden="true"><path d="m1 1 8 8m0-8L1 9" stroke="currentColor" stroke-linecap="round"/></svg>' +
              '</button>' +
            '</div>' +
          '</div>' +
          priceBlock(item);

        row.querySelector("[data-cart-qty-dec]").addEventListener("click", function () {
          changeQty(item.key, item.quantity - 1);
        });
        row.querySelector("[data-cart-qty-inc]").addEventListener("click", function () {
          changeQty(item.key, item.quantity + 1);
        });
        row.querySelector("[data-cart-item-remove]").addEventListener("click", function () {
          changeQty(item.key, 0);
        });
        itemsEl.appendChild(row);
      });
    }

    function renderTotals(cart) {
      // cart.total_price already has the REAL discount applied once (the
      // Shopify Function is live) — it's authoritative, not an estimate.
      // Previously we subtracted our own estimated discount from it a
      // second time, double-counting (Nicolas/Víctor, 22-Jul). Subtotal
      // must be the pre-discount sum (original_total_price), and Total is
      // just cart.total_price as-is; the discount row is their difference,
      // not our own re-estimate, so it always matches checkout exactly.
      var originalTotal = cart.original_total_price != null ? cart.original_total_price : cart.total_price;
      var realDiscountCents = Math.max(0, originalTotal - cart.total_price);

      subtotalEl.textContent = money(originalTotal);

      root.querySelectorAll("[data-pkg-cart-discount-row]").forEach(function (el) { el.remove(); });

      if (realDiscountCents > 0 && totalRowEl) {
        var apps = cartDiscountApplications(cart);
        if (!apps.length) apps = [{ title: "Discount", total_allocated_amount: realDiscountCents }];
        apps.forEach(function (app) {
          var row = document.createElement("div");
          row.className = "pkg-cart__row pkg-cart__row--discount";
          row.setAttribute("data-pkg-cart-discount-row", "");
          row.innerHTML =
            '<span>' + escapeHtml(discountLabel(app, "row")) + '</span>' +
            '<span>-' + money(app.total_allocated_amount) + '</span>';
          totalRowEl.parentNode.insertBefore(row, totalRowEl);
        });
      }

      totalEl.textContent = money(cart.total_price);
    }

    function render(cart) {
      var IM = window.IronmasterDiscount;
      var draw = function () {
        renderItems(cart);
        renderTotals(cart);
      };
      var handles = (cart.items || []).map(function (i) { return i.handle; });

      if (IM && IM.fetchProductTagsForHandles) {
        IM.fetchProductTagsForHandles(handles)
          .then(draw)
          .catch(function () {
            fetchCompareAtForHandles(handles).then(function () { draw(null); });
          });
      } else {
        fetchCompareAtForHandles(handles).then(function () { draw(null); });
      }
    }

    function changeQty(key, qty) {
      if (busy) return;
      busy = true;
      root.classList.add("is-loading");
      fetch("/cart/change.js", {
        method: "POST",
        headers: { "Content-Type": "application/json", "Accept": "application/json" },
        body: JSON.stringify({ id: key, quantity: Math.max(0, qty) })
      })
        .then(function (r) { if (!r.ok) throw new Error("cart change failed"); return r.json(); })
        .then(function (cart) {
          setCartError(null);
          render(cart);
          document.dispatchEvent(new CustomEvent("cart:refresh", { bubbles: true }));
        })
        .catch(function (err) {
          console && console.warn && console.warn("[PkgCart]", err);
          setCartError(qty <= 0
            ? "We couldn't remove this item. Please try again."
            : "We couldn't update this item. Please try again.");
        })
        .then(function () { busy = false; root.classList.remove("is-loading"); });
    }

    // Shows the cart's own spinner whenever anything (this cart's own qty
    // buttons, or an add/remove elsewhere like the builder cards) triggers a
    // cart:change/cart:refresh — otherwise the panel just sits still for the
    // length of this fetch, which reads as unresponsive.
    function refresh() {
      root.classList.add("is-loading");
      fetch("/cart.js", { headers: { Accept: "application/json" } })
        .then(function (r) { return r.json(); })
        .then(render)
        .catch(function (err) { console && console.warn && console.warn("[PkgCart]", err); })
        .then(function () { root.classList.remove("is-loading"); });
    }

    var codeInput = root.querySelector("[data-cart-code-input]");
    var codeApplyBtn = root.querySelector("[data-cart-code-apply]");
    if (codeInput && codeApplyBtn) {
      codeInput.addEventListener("input", function () {
        codeApplyBtn.hidden = codeInput.value.trim().length === 0;
      });
    }

    if (codeForm) {
      codeForm.addEventListener("submit", function (e) {
        e.preventDefault();
        var code = (codeForm.querySelector('input[name="discount"]').value || "").trim();
        if (!code) return;
        // No pure-AJAX "apply discount code" endpoint exists pre-checkout —
        // this is the standard Shopify deep link that applies the code.
        // Redirect to /cart (not /checkout) so the customer sees it reflected
        // on the real cart page first, instead of being pushed straight to payment.
        window.location.href = "/discount/" + encodeURIComponent(code) + "?redirect=/cart";
      });
    }
    if (checkoutLink) {
      checkoutLink.setAttribute("href", "/checkout");
    }

    document.addEventListener("cart:change", refresh);
    document.addEventListener("cart:refresh", refresh);
    refresh();
  }

  function boot() {
    document.querySelectorAll("[data-pkg-cart]").forEach(init);
  }
  if (document.readyState !== "loading") boot();
  else document.addEventListener("DOMContentLoaded", boot);
})();
